/* Purpose: Pop up for admin to view, add, and delete admin notes and notes for clients
 * Props: loan (map)
 *        setLoan (setState)
 *        credentials (map)
 *        username (str)
 *        allClients (list of map)
 *        setAllClients (setState)
 *        allLoans (list)
 *        setAllLoans (setState)
 *        isAdmin (boolean)
 *        isFromReview (boolean) --optional
 *        allLoansR (list) --optional
 *        setAllLoansR (setState) --optional
 *        uploadedFiles (map) --optional
 *        onHide (func)
 * Author: R. Markowitz (May 2025)
 * Dev Notes: 
*/

import {Badge, Button, Spinner, Stack} from 'react-bootstrap';
import Modal from 'react-bootstrap/Modal';
import React, {useState,useEffect} from 'react';
import { Form, Image, Container, Row, Col } from 'react-bootstrap';
import { refiFiles, purchaseFiles, fixAndFlipFiles } from '../Data/RequiredLoanData';
import SubmitButton from './Button';
import { longTextInvalidMsg, longTextRegex, validateField } from '../Data/Regex';

const NoteModal = (props) => {
    const loan = props.loan;
    const setLoan = props.setLoan;
    const credentials = props.credentials
    const username = props.username;
    const allClients = props.allClients
    const setAllClients = props.setAllClients;
    const allLoans = props.allLoans
    const setAllLoans = props.setAllLoans;
    const isFromReview = props.isFromReview
    const allLoansR = props.allLoansR
    const setAllLoansR = props.setAllLoansR
    const uploadedFiles = props.uploadedFiles;
    const isAdmin = props.isAdmin
    const deleteIcon = require("../Images/deleteSmall.png")
    const [radioSelection, setRadioSelection] = useState(isAdmin?"All":"Client")
    const [fileName, setFileName] = useState("General")
    const [fileList, setFileList] = useState([])
    const [addText, setAddText] = useState({"AdminNotes":"Add Note", "ClientFeedback":"Add Note"})
    const [previewFileCache, setPreviewFileCache] = useState([]);

    // If the loan does not have AdminNotes or clientFeedback, add it to the loan
    useEffect(() => {
        let tempLoan = {...loan}
        if (!loan.AdminNotes){
            tempLoan['AdminNotes'] = {}
        }

        if (!loan.ClientFeedback){
            tempLoan['ClientFeedback'] = {}
        }

        if (!loan.FileStatus){
            tempLoan['FileStatus'] = {}
        }
        
        setLoan(tempLoan)
    },[])

    // Set the file list according to the type of loan
    useEffect(() => {
        let tempFileList = ["General"]
        if (loan.LoanType == "Refinance"){
            refiFiles.forEach((file) => tempFileList.push(file.fileName))
        }else if(loan.LoanType == "Purchase"){
            purchaseFiles.forEach((file) => tempFileList.push(file.fileName))
        }else if(loan.LoanType == "Fix and Flip"){
            fixAndFlipFiles.forEach((file) => tempFileList.push(file.fileName))
        }else{
            alert("ERROR. Loan Type Invalid.")
        }

        setFileList(tempFileList);
    },[loan.LoanType])

    
    const handleRadioChange = (value) => {
        setRadioSelection(value);
    }

    const handleSelectChange = (value) => {
        setFileName(value)
    }

    const handleAddNote = async(noteType, fieldName) => {
        let tempAddText = {...addText, [noteType]:"Adding Note..."}
        setAddText(tempAddText)
        let id = fieldName?(noteType + "-" + fieldName):(noteType)
        let newNote = document.getElementById(id).value
        if(newNote == ""){
            alert("Can not add an empty note.")
        }else if (!validateField({value: newNote, regex: longTextRegex})){
            alert(longTextInvalidMsg)
        }else{
            newNote += "(" + username + ")"
            fieldName = fieldName?fieldName:fileName

            let tempNotes = loan[noteType]? {...loan[noteType]}:{}
            tempNotes[fieldName] = tempNotes[fieldName]? [...tempNotes[fieldName], newNote]:[newNote]

            // Add the note to dynamodb
            const url = "https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/updateLoanNotes"
            const params = {
                method:"POST",
                body: JSON.stringify({
                    "ClientID": loan.ClientID,
                    "LoanID": loan.LoanID,
                    "noteType": noteType,
                    "updateNoteList": tempNotes,
                    "updatedFileName": fieldName,
                    "credentials": credentials
                })
            };

            try{
                const response = await fetch(url, params);
                if (response.ok){
                    // Update the loan that is open now
                    let tempLoan = {...loan, [noteType]:tempNotes}
                    setLoan(tempLoan)

                    // Update the loan in loan list so the loan will have the new note 
                    // even after the modal is closed and before the client list is refreshed
                    let tempAllLoans = [...allLoans]
                    const loanIndex = tempAllLoans.findIndex((l) => l.LoanID == loan.LoanID)
                    tempAllLoans[loanIndex] = tempLoan
                    setAllLoans(tempAllLoans)

                    //update the client list
                    let tempAllClients = [...allClients]
                    const loanInClientIndex = tempAllClients[loan.ClientIndex]['Loans'].findIndex((l) => l.LoanID == loan.LoanID)
                    tempAllClients[loan.ClientIndex]['Loans'][loanInClientIndex] = tempLoan
                    setAllClients(tempAllClients)

                    if(isFromReview){
                        let tempAllLoansR = [...allLoansR];
                        const loanIndexR = tempAllLoansR.findIndex((l) => l.LoanID == loan.LoanID)
                        tempAllLoansR[loanIndexR] = tempLoan
                        setAllLoansR(tempAllLoansR)
                    }
                

                    // clear the form control
                    document.getElementById(id).value = "";
                }else{
                    let errorMsg = response.statusText
                    if (response.status == 400){
                        errorMsg = await response.json()
                    }
                    console.log("Something went wrong while trying to add a note. ", errorMsg)
                    alert("Something went wrong while trying to add the note. ERROR: " + errorMsg)
                    tempAddText[noteType] = "Add Note"
                    setAddText(tempAddText)
                }
            }catch (e){
                console.error("Something went wrong while trying to add the note. ", e)
                alert("Something went wrong while trying to add the note. ERROR " + e)
                tempAddText[noteType] = "Add Note"
                setAddText(tempAddText)
            }
        }
        tempAddText[noteType] = "Add Note"
        setAddText(tempAddText)
    }

    const handleDeleteNote = async(noteType, noteIndex, fieldName) => {
        fieldName = fieldName?fieldName:fileName
        let tempNotes = {...loan[noteType]}
        tempNotes[fieldName].splice(noteIndex, 1)

        // Remove note from dynamodb
        const url = "https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/updateLoanNotes"
        const params = {
            method:"POST",
            body: JSON.stringify({
                "ClientID": loan.ClientID,
                "LoanID": loan.LoanID,
                "noteType": noteType,
                "updateNoteList": tempNotes,
                "credentials": credentials
            })
        };

        try{
            const response = await fetch(url, params);
            if (response.ok){
                // Update the loan that is open now
                let tempLoan = {...loan, [noteType]:tempNotes}
                setLoan(tempLoan)

                // Update the loan in loan list so the loan will have the new note 
                // even after the modal is closed and before the client list is refreshed
                let tempAllLoans = [...allLoans]
                const loanIndex = tempAllLoans.findIndex((l) => l.LoanID == loan.LoanID)
                tempAllLoans[loanIndex] = tempLoan
                setAllLoans(tempAllLoans)

                //update the client list
                let tempAllClients = [...allClients]
                const loanInClientIndex = tempAllClients[loan.ClientIndex]['Loans'].findIndex((l) => l.LoanID == loan.LoanID)
                tempAllClients[loan.ClientIndex]['Loans'][loanInClientIndex] = tempLoan
                setAllClients(tempAllClients)
                
                if(isFromReview){
                    let tempAllLoansR = [...allLoansR];
                    const loanIndexR = tempAllLoansR.findIndex((l) => l.LoanID == loan.LoanID)
                    tempAllLoansR[loanIndexR] = tempLoan
                    setAllLoansR(tempAllLoansR)
                }

            }else{
                console.log("Something went wrong while trying to delete a note. ", response.statusText)
                alert("Something went wrong while trying to delete the note. ERROR: " + response.statusText)
            }
        }catch (e){
            console.error("Something went wrong while trying to delete the note. ", e)
            alert("Something went wrong while trying to delete the note. ERROR " + e)
        }
    }

    // When click on a fileName to preview, display the file in a seperate tab and cache the file
    const handlePreviewFile = (filePath) => {
        const handleClickedPreview = async (filePath) => {

            try{
                const urlResponse = await fetch('https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/downloadFileFromS3', {
                "method": "POST",
                "body":JSON.stringify({
                    "filePath":filePath,
                    "credentials": credentials
                })
                });
                if (urlResponse.ok){
                const presignedData = await urlResponse.json();
                const fetchedFileResponse = await fetchFileFromS3(presignedData, filePath);

                }else{
                console.log("Something went wrong when trying to get a presigned url. response not ok.")
                alert("An error occured while retireiving the file for viewing.")
                }
            }catch (error){
                console.error("Something went wrong when getting or using the presigned url to view the file. Error: ", error)
                alert("An error occured while retireiving the file for viewing.")
            }        
        };
        
        const fetchFileFromS3 = async (urlData, fileName) => {
            try{     
                const response = await fetch(urlData, {responseType: "arrayBuffer"});
                
                if (response.ok){
                const blob = await response.blob();
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
                // cache the file
                setPreviewFileCache([...previewFileCache, {url, name: fileName}]);
    
    
                }else{
                console.error("Something went wrong when fetching the file. Response not ok!")
                alert("An error occured while retireiving the file for viewing.")
                }
    
            }catch (error){
                console.error("Something went wrong when trying to view the file.",  error)
                alert("An error occured while retireiving the file for viewing.")
            }
        }
        
        
            
        const cacheIndex =  previewFileCache.findIndex(f => f.name === filePath);
        
        // If the file is cached, open it from fileCache.
        // If not, call handleClickedPreview to fetch from s3
        if (cacheIndex !== -1){
            console.log("Opening file from cache...");
            let url  = previewFileCache[cacheIndex].url
            window.open(url, '_blank');
        }else{
            console.log("File not cached. Fetching file from S3...");
            handleClickedPreview(filePath);
        }      

    }

    //When change the status of a file,
    const handleFileStatusChange = async(filePath, value) => {
        let tempLoan = {...loan}
        tempLoan['FileStatus'][filePath] = value

        // Save the fileStatus change to dynamodb
        try{
            const response = await fetch("https://f0ll5x1c4l.execute-api.us-east-1.amazonaws.com/updateFileStatus", {
                "method": "POST",
                "body": JSON.stringify({
                    "filePath": filePath,
                    "updatedStatusMap": tempLoan.FileStatus,
                    "credentials": credentials
                })
            })

            if(response.ok){
                let msg = await response.json();
                setLoan(tempLoan);
            }else{
                let error = response.statusText
                if (response.status == 400){
                    error = await response.json();
                }
                alert("Something went wrong while updating the status of this file. " + error)
                console.log(error)
            }
        }catch (e){
            alert("Something went wrong while updating the status of this file. " + e);
            console.log(e)
        }
        
    }

    return(
        <Modal
        show = {true}
        onHide = {props.onHide}
        aria-labelledby="contained-modal-title-vcenter"
        centered
        size='lg'
        >
        <Modal.Header closeButton>
            <Modal.Title>
                {loan.LoanID} {isFromReview && ("Files and ")}Notes
                <span className='font-size-s'>        Client: {loan.ClientName}</span>
            </Modal.Title>
            
        </Modal.Header>

        <Modal.Body>
            {isAdmin && (<Form >
                    <Form.Check
                        inline
                        label="Admin Notes"
                        type="radio"
                        id="ForAdmin"
                        value = "Admin"
                        checked = {radioSelection == "Admin"}
                        onChange={(event) => handleRadioChange(event.currentTarget.value)}
                    />
                    <Form.Check
                        inline
                        label="Client Notes"
                        type="radio"
                        id="ForClient"
                        value = "Client"
                        checked = {radioSelection == "Client"}
                        onChange={(event) => handleRadioChange(event.currentTarget.value)}
                    />
                    <Form.Check
                        inline
                        label="All Notes"
                        type="radio"
                        id="All"
                        value = "All"
                        checked = {radioSelection == "All"}
                        onChange={(event) => handleRadioChange(event.currentTarget.value)}
                    />
            </Form>)}
            {isFromReview?(
            <>
                <Form.Select className='my-2' aria-label="sort-options" value = {fileName} onChange = {(event) => handleSelectChange(event.currentTarget.value)}>
                    {fileList.map((file, index) => (
                        <>
                            {(file == "General" || (uploadedFiles && uploadedFiles[file] && uploadedFiles[file].length > 0)) && (
                                    <option key = {index} value = {file} id = {file}>{file}</option>
                            )}
                        </>
                    ))}
                </Form.Select>
            

                {(fileName != "General" && isFromReview && uploadedFiles) && (
                    <div className = "mt-3"><span className='bold'>Uploaded File(s):</span>
                        <ul>
                            {(uploadedFiles[fileName] && uploadedFiles[fileName].length > 0)?(
                                uploadedFiles[fileName].map((filePath, index) => (
                                <Row xs={1} md = {2} lg= {2}>
                                    <Col xs = {12} lg = {8}>
                                        <a href="#files" onClick = {() => handlePreviewFile(filePath)}>
                                            <li key = {index}>
                                                {filePath.split("/")[1].split("_")[2]}
                                            </li>
                                        </a>
                                    </Col>
                                    {isAdmin? (<Col xs = {12} lg = {4}>
                                        <div class="btn-group btn-group-sm" role="group" aria-label="Basic radio toggle button group">
                                            <input 
                                                type="radio" 
                                                class="btn-check" 
                                                id={"reject-" + filePath} 
                                                onChange = {() => handleFileStatusChange(filePath, "Rejected")}
                                                checked = {loan.FileStatus[filePath] && loan.FileStatus[filePath] == "Rejected"}
                                            />
                                            <label class="btn btn-outline-danger" for={"reject-" + filePath}>{loan.FileStatus[filePath] && loan.FileStatus[filePath] == "Rejected"?"Rejected":"Reject"}</label>

                                            <input 
                                                type="radio" 
                                                class="btn-check" 
                                                id={"null-" + filePath} 
                                                onChange = {() => handleFileStatusChange(filePath, "")} 
                                                checked = {!loan.FileStatus[filePath]}
                                            />
                                            <label class="btn btn-outline-light" for={"null-" + filePath}> </label>

                                            <input 
                                                type="radio" 
                                                class="btn-check" 
                                                id={"approve-" + filePath}
                                                onChange = {() => handleFileStatusChange(filePath, "Approved")} 
                                                checked = {loan.FileStatus[filePath] && loan.FileStatus[filePath] == "Approved"}
                                            />
                                            <label className="btn btn-outline-success" htmlFor={"approve-" + filePath}>{loan.FileStatus[filePath] && loan.FileStatus[filePath] == "Approved"?"Approved":"Approve"}</label>
                                        </div>
                                    </Col>
                                    ):(
                                        <Col xs = {12} md= {4} lg ={4}>
                                            {loan.FileStatus[filePath] && (
                                                <Badge pill bg = {loan.FileStatus[filePath] == "Approved"?"success":"danger"}>{loan.FileStatus[filePath]}</Badge>
                                            )}
                                        </Col>
                                    )}          
                                </Row>
                            ))):(
                                <li>No files were uploaded yet.</li>
                            ) }
                        </ul>
                    </div>
                )}

                {(radioSelection == "Admin" || radioSelection == "All") && loan.AdminNotes && (
                    <div className = "mt-3"><span className='bold'>Admin Notes: </span>
                        <ul>
                            {(loan.AdminNotes[fileName] && loan.AdminNotes[fileName].length > 0?(
                                loan.AdminNotes[fileName].map((note, index) => (
                                <Row className = "my-4" xs={2} key ={index}>
                                    <Col xs={1}>
                                        <a className = "ms-1" onClick = {() => {handleDeleteNote("AdminNotes", index)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                    </Col>
                                    <Col xs={11}>
                                        <>
                                            {note.includes("(")?(
                                                <>
                                                    {note.substring(0, note.lastIndexOf("("))}      <span className='admin-name-stamp'>{note.substring(note.lastIndexOf("("))}</span>
                                                </>
                                            ):(<>{note}</>)}
                                        </>
                                    </Col>
                                </Row>
                            ))):(
                                <Row>No Admin Notes</Row>
                            ))}
                        </ul>

                        <Form.Control as="textarea" rows={3} id = "AdminNotes" placeholder='New Note'></Form.Control>
                        <SubmitButton text = {addText.AdminNotes} onClick = {() => {handleAddNote("AdminNotes")}} width = "10" />
                    </div>
                )}
                {(radioSelection == "Client" || radioSelection == "All") && loan.ClientFeedback &&(
                    <div><span className='bold'>{isAdmin?("Feedback for Client: "):("Comments: ")}</span>
                        <ul>
                            {(loan.ClientFeedback[fileName] && loan.ClientFeedback[fileName].length > 0? (
                                loan.ClientFeedback[fileName].map((note, index) => (
                                <div key={index}>
                                    {isAdmin?(<Row className = "my-4" xs={2} key ={index}>
                                        <Col xs={1}>
                                            <a className = "noteDelete" onClick = {() => {handleDeleteNote("ClientFeedback", index)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                        </Col>
                                        {note.includes("(")?(
                                                <>
                                                    {note.substring(0, note.lastIndexOf("("))}      <span className='admin-name-stamp'>{note.substring(note.lastIndexOf("("))}</span>
                                                </>
                                        ):(<>{note}</>)}
                                        
                                    </Row>
                                    ):(
                                        <>
                                        {note.includes("(")?(
                                            <li className='mt-1'>{note.substring(0, note.lastIndexOf("("))}</li>
                                        ):(
                                            <li className='mt-1'>{note}</li>
                                        )}
                                        </>
                                    )}
                                </div>
                            ))):(
                                <Row>No Comments</Row>
                            ))}
                        </ul>
                        
                        {isAdmin && (
                            <div>
                                <Form.Control as="textarea" rows={3} id = "ClientFeedback" placeholder='New Note'></Form.Control>
                                <SubmitButton text = {addText.ClientFeedback} onClick = {() => {handleAddNote("ClientFeedback")}} width = "10" />
                            </div>)}
                    </div>
                    )}
                    
            </>
            ):(
                <div>
                {fileList.map((field, index) => (
                    <div key = {index} className='my-4'>
                    <span className = "underline bold font-size-l">{field}</span>
                    <Container>
                        {((radioSelection == "Admin" || radioSelection == "All") && loan.AdminNotes) && (
                            <div className = "mt-3"><span className='bold'>Admin Notes: </span>
                                <ul>
                                    {(loan.AdminNotes[field] && loan.AdminNotes[field].length > 0?(
                                        loan.AdminNotes[field].map((note, index) => (
                                        <Row className = "my-4" xs={2} key ={index}>
                                            <Col xs={1}>
                                                <a className = "ms-1" onClick = {() => {handleDeleteNote("AdminNotes", index, field)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                            </Col>
                                            <Col xs={11}>
                                                <>
                                                    {note.includes("(")?(
                                                        <>
                                                            {note.substring(0, note.lastIndexOf("("))}      <span className='admin-name-stamp'>{note.substring(note.lastIndexOf("("))}</span>
                                                        </>
                                                    ):(<>{note}</>)}
                                                </>
                                            </Col>
                                        </Row>
                                    ))):(
                                        <Row>No Admin Notes</Row>
                                    ))}
                                </ul>

                                <Row xs ={2}>
                                    <Col xs = {9}>
                                        <Form.Control as="textarea" rows={2} id = {"AdminNotes-" + field} placeholder='New Note'></Form.Control>
                                    </Col>
                                    <Col xs = {3}>
                                        <SubmitButton text = {addText.AdminNotes} onClick = {() => {handleAddNote("AdminNotes", field)}} width = "10" />
                                    </Col>

                                </Row>
                            </div>
                        )}

                        {(radioSelection == "Client" || radioSelection == "All") && loan.ClientFeedback &&(
                        <div><span className='bold'>Feedback for Client: </span>
                            <ul>
                                {(loan.ClientFeedback[field] && loan.ClientFeedback[field].length > 0? (
                                    loan.ClientFeedback[field].map((note, index) => (
                                    <div key={index}>
                                        <Row className = "my-4" xs={2} key ={index}>
                                            <Col xs={1}>
                                                <a className = "noteDelete" onClick = {() => {handleDeleteNote("ClientFeedback", index, field)}}><Image alt = "Delete" src= {deleteIcon} fluid/></a>
                                            </Col>
                                            <Col xs={11}>
                                                {note.includes("(")?(
                                                <>
                                                    {note.substring(0, note.lastIndexOf("("))}      <span className='admin-name-stamp'>{note.substring(note.lastIndexOf("("))}</span>
                                                </>
                                                ):(<>{note}</>)}
                                            </Col>
                                            
                                        </Row>
                                    </div>
                                ))):(
                                    <Row>No Comments</Row>
                                ))}
                            </ul>
                            
                            
                            <Row xs = {2}>
                                <Col xs = {9}>
                                <Form.Control as="textarea" rows={2} id = {"ClientFeedback-" + field} placeholder='New Note'></Form.Control>
                                </Col>
                                <Col xs = {3}>
                                <SubmitButton text = {addText.ClientFeedback} onClick = {() => {handleAddNote("ClientFeedback", field)}} width = "10" />
                                </Col>
                            </Row>
                        </div>
                        )}

                    </Container>
                    </div>
                ))}
                </div>
            )}
        </Modal.Body>
        <Modal.Footer>
            <Button onClick={props.onHide}>Close</Button>
        </Modal.Footer>
        </Modal>
    );
};

export default NoteModal; 